import { useState } from 'react'
import { MapPin, Phone, Globe, Car, Clock, Tag, Copy, Check, Mail, MessageCircle } from 'lucide-react'
import { useVersion } from '../contexts/VersionContext'

const accommodations = [
  {
    id: 1,
    name: 'Hotel Boutique Na\'ura Centro',
    type: 'hotel',
    description: 'Located just 2 blocks from the Zócalo, our colonial-style boutique hotel is characteristic of the splendid architecture of a city with a rich history. Na\'ura has been providing comfort and quality service for over 30 years to all our guests and clients.',
    address: 'Miguel Hidalgo 918, Centro, 68000 Oaxaca de Juárez, Oax., Mexico',
    website: 'https://www.hotelnaura.com/',
    phone: '+52 951 501 5400',
    distance: '15-20 min drive to venue or airport',
    promoCode: 'Naetzy & Eduardo',
    promoRate: '$3,570 - $6,247 MXN per night',
    promoContact: {
      phone: '+529515015400',
      email: 'reservaciones@hotelnaura.com',
    },
    promoInstructions: [
      'Call +529515015400 or email reservaciones@hotelnaura.com to start the reservation.',
      'Let them know you are booking for Naetzy & Eduardo\'s wedding weekend and share the promo code.',
      'Confirm the dates and room preference once the code is applied.',
    ],
  },
  {
    id: 2,
    name: 'Hotel ABU',
    type: 'hotel',
    description: 'A new hotel concept located in the historic center of Oaxaca, Hotel ABU blends the understated elegance of its surrounding quarry stone with a modern yet inviting interior design, creating a space where you feel like the protagonist of an experience crafted especially for you.',
    address: 'Murguía 104, RUTA INDEPENDENCIA, Centro, 68000 Oaxaca de Juárez, Oax., Mexico',
    website: 'https://hotelabu.com/',
    phone: '+52 951 516 4900',
    distance: '20-25 min drive to venue or airport',
    promoCode: 'Boda Naetzy & Eduardo',
    promoRate: '$1,784.75 - $2,756.48 MXN per night',
    promoContact: {
      phone: '+529515164900',
      email: 'reservaciones@hotelabu.com',
    },
    promoInstructions: [
      'Call +529515164900 or email reservaciones@hotelabu.com to start the reservation.',
      'Mention you are booking for Naetzy & Eduardo\'s wedding weekend and provide the promo code.',
      'Confirm the dates and room preference once the code is applied.',
    ],
  },
  {
    id: 3,
    name: 'Grand Fiesta Americana Oaxaca',
    type: 'hotel',
    description: 'Grand Fiesta Americana Oaxaca offers a unique experience that will captivate you. Our hotel is an architectural wonder, where tradition and modernity blend to create an atmosphere of luxury and exclusivity.',
    address: 'Calle de José María Pino Suárez 702, RUTA INDEPENDENCIA, Centro, 68000 Oaxaca de Juárez, Oax., Mexico',
    website: 'https://www.corpo-rate.com/login?groupId=G1UY6S@FGO&lang=EN',
    phone: '+52 443 310 8137',
    distance: '15-20 min drive to venue or airport',
    promoCode: 'G1UY6S@FGO',
    promoRate: '$7,140.00 MXN per night',
    promoContact: {
      phone: '+524433108019',
      whatsapp: '+524431378728',
      email: 'banquetesfgoa@posadas.com',
    },
    promoInstructions: [
      'Book online at the link below and be sure to switch the currency to MXN (USD will not work).',
      'Alternatively, call +524433108019 (or WhatsApp +524431378728) or email banquetesfgoa@posadas.com and mention the group/wedding booking for Naetzy & Eduardo with code G1UY6S@FGO.',
      'Confirm your dates and room preference once the code is applied.',
    ],
  },
  {
    id: 4,
    name: 'Hotel Casa Cantera',
    type: 'hotel',
    description: 'Strategically located in the heart of the city, it is ideal for discovering and experiencing Oaxaca\'s traditions and attractions, as well as for business trips, all within a relaxed atmosphere and modern design.',
    address: 'Priv. de Reforma 105, RUTA INDEPENDENCIA, Centro, 68000 Oaxaca de Juárez, Oax., Mexico',
    website: 'http://hotelcasacantera.com.mx/',
    phone: '+52 951 515 4989',
    distance: '20-25 min drive to venue or airport',
    promoCode: 'Boda Naetzy & Eduardo',
    promoRate: '$1,500 - $2,350 MXN per night',
    promoContact: {
      phone: '+529515154989',
      email: 'hotelcasacantera.oax@gmail.com',
    },
    promoInstructions: [
      'Call +529515154989 (preferred) or email hotelcasacantera.oax@gmail.com (slower response time) to start the reservation.',
      'Mention you are booking for Naetzy & Eduardo\'s wedding weekend and provide the promo code.',
      'Confirm the dates and room preference once the code is applied.',
    ],
  },
  {
    id: 5,
    name: 'Airbnb Options',
    type: 'airbnb',
    description: 'Various apartment and house options throughout the historic center.',
    address: 'Centro Histórico, Oaxaca de Juárez, Oaxaca, Mexico',
    website: 'https://airbnb.com',
    phone: '',
    distance: '10-25 min drive to venue',
  },
]

export function Accommodation() {
  const { isV1 } = useVersion()
  const [copiedCodeId, setCopiedCodeId] = useState<number | null>(null)

  const handleCopyCode = async (code: string, accommodationId: number) => {
    try {
      await navigator.clipboard.writeText(code)
      setCopiedCodeId(accommodationId)
      setTimeout(() => setCopiedCodeId(null), 2000)
    } catch (err) {
      console.error('Failed to copy code:', err)
    }
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-serif text-primary-900 mb-4">
              Accommodations + Travel
            </h1>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              {isV1 
                ? "We're working hard to curate the perfect selection of places to stay in beautiful Oaxaca."
                : "We've curated a selection of beautiful places to stay in Oaxaca. Book early to ensure availability for our wedding weekend!"
              }
            </p>
          </div>

          {isV1 ? (
            // Coming Soon Component for V1
            <div className="flex flex-col items-center justify-center py-16">
              <div className="card p-12 max-w-2xl mx-auto text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-6">
                  <Clock className="h-8 w-8 text-primary-600" />
                </div>
                
                <h2 className="text-3xl font-serif text-primary-900 mb-4">
                  Accommodations Coming Soon!
                </h2>
                
                <p className="text-lg text-neutral-600 mb-6">
                  We're currently finalizing our recommended accommodations for our wedding weekend in Oaxaca. 
                  Check back soon for detailed recommendations on where to stay!
                </p>
              </div>
            </div>
          ) : (
            // Full Accommodations Content for Default Version
            <div>
              <h2 className="text-3xl font-serif text-primary-900 mb-8 text-center">
                Accommodations
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {accommodations.map((accommodation) => (
                  <div key={accommodation.id} className="card flex flex-col h-full">
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-xl font-serif text-primary-900">
                          {accommodation.name}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wide flex-shrink-0 ml-3 ${
                          accommodation.type === 'hotel' 
                            ? 'bg-primary-100 text-primary-700' 
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {accommodation.type}
                        </span>
                      </div>
                      
                      <p className="text-neutral-600 mb-4 text-sm leading-relaxed flex-1">
                        {accommodation.description}
                      </p>

                      <div className="space-y-2 mb-6">
                        <div className="flex items-start gap-2 text-sm text-neutral-600">
                          <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <span className="leading-tight">{accommodation.address}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-neutral-600">
                          <Car className="h-4 w-4 flex-shrink-0" />
                          <span>{accommodation.distance}</span>
                        </div>
                        {accommodation.phone && !accommodation.promoContact && (
                          <div className="flex items-center gap-2 text-sm text-neutral-600">
                            <Phone className="h-4 w-4 flex-shrink-0" />
                            <span>{accommodation.phone}</span>
                          </div>
                        )}
                        {accommodation.promoCode && (
                          <div className="mt-4 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                            <div className="flex items-center gap-2 text-sm">
                              <Tag className="h-4 w-4 text-primary-600 flex-shrink-0" />
                              <div className="flex-1">
                                <span className="font-medium text-primary-900">Group/Promo Code:</span>
                                <span className="ml-2 font-mono text-primary-700">{accommodation.promoCode}</span>
                              </div>
                              <button
                                onClick={() => handleCopyCode(accommodation.promoCode!, accommodation.id)}
                                className="p-1.5 hover:bg-primary-100 rounded transition-colors flex-shrink-0"
                                aria-label="Copy promo code"
                                title="Copy promo code"
                              >
                                {copiedCodeId === accommodation.id ? (
                                  <Check className="h-4 w-4 text-primary-600" />
                                ) : (
                                  <Copy className="h-4 w-4 text-primary-600" />
                                )}
                              </button>
                            </div>
                              {accommodation.promoRate && (
                                <div className="mt-2 text-sm text-primary-800">
                                  <span className="font-semibold">Estimated rate w/ code:</span>{' '}
                                  <span className="font-normal">{accommodation.promoRate}</span>
                                </div>
                              )}
                            {accommodation.promoInstructions && (
                              <div className="mt-3 space-y-2 text-sm text-neutral-600">
                                <p className="text-sm font-semibold text-primary-900">How to book:</p>
                                <ol className="list-decimal list-inside space-y-1 text-sm text-neutral-600">
                                  {accommodation.promoInstructions.map((step, index) => (
                                    <li key={index}>{step}</li>
                                  ))}
                                </ol>
                                {accommodation.promoContact && (
                              <div className="space-y-2 text-sm text-neutral-600">
                                    <div className="flex items-center gap-2">
                                      <Phone className="h-4 w-4 text-primary-600" />
                                      <a
                                        href={`tel:${accommodation.promoContact.phone}`}
                                        className="font-medium text-primary-700 underline-offset-2 hover:underline"
                                      >
                                        {accommodation.promoContact.phone}
                                      </a>
                                      <span className="text-neutral-500">call</span>
                                    </div>
                                {accommodation.promoContact.whatsapp && (
                                  <div className="flex items-center gap-2">
                                    <MessageCircle className="h-4 w-4 text-primary-600" />
                                    <a
                                      href={`https://wa.me/${accommodation.promoContact.whatsapp.replace(/\D/g, '')}`}
                                      className="font-medium text-primary-700 underline-offset-2 hover:underline"
                                    >
                                      {accommodation.promoContact.whatsapp}
                                    </a>
                                    <span className="text-neutral-500">WhatsApp</span>
                                  </div>
                                )}
                                    <div className="flex items-center gap-2">
                                      <Mail className="h-4 w-4 text-primary-600" />
                                      <a
                                        href={`mailto:${accommodation.promoContact.email}`}
                                        className="font-medium text-primary-700 underline-offset-2 hover:underline"
                                      >
                                        {accommodation.promoContact.email}
                                      </a>
                                      <span className="text-neutral-500">email</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="mt-auto">
                        <a
                          href={accommodation.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-primary w-full text-center text-sm inline-flex items-center justify-center py-3"
                        >
                          <Globe className="h-4 w-4 mr-2" />
                          Visit Website
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Travel Information */}
          <div className="mt-16">
            <div className="card p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-serif text-primary-900 mb-6 text-center">
                Travel Information
              </h3>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-lg font-semibold text-primary-900 mb-3">
                    Getting to Oaxaca
                  </h4>
                  <div className="space-y-3 text-neutral-600">
                    <p>
                      <strong>Airport:</strong> Xoxocotlán International Airport (OAX) 
                      is about 20-30 minutes from the city center.
                    </p>
                    <p>
                      <strong>Transportation:</strong> Taxi or airport shuttle services 
                      are available from the airport to your hotel.
                    </p>
                    <p>
                      <strong>Currency:</strong> Mexican Peso (MXN).
                    </p>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold text-primary-900 mb-3">
                    Local Tips
                  </h4>
                  <div className="space-y-3 text-neutral-600">
                    <p>
                      <strong>Weather:</strong> April is a beautiful month in Oaxaca with temperatures around 75-85°F (24-29°C).
                      Perfect weather for celebrating!
                    </p>
                    <p>
                      <strong>Language:</strong> Spanish is the official language. 
                      Many people in tourist areas speak English.
                    </p>
                    <p>
                      <strong>Safety:</strong> The historic center is a safe, well-patrolled area. 
                      Use common sense and stay in well-lit areas at night.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {copiedCodeId !== null && (
        <div className="fixed bottom-6 right-6 z-50 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="bg-primary-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2">
            <Check className="h-5 w-5" />
            <span className="font-medium">Promo code copied to clipboard!</span>
          </div>
        </div>
      )}
    </div>
  )
}